import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CardScreen, InstructionStep } from '../common/CardScreen';
import { t } from '@/strings/index';
import { AppDispatch } from '@/store';
import { PolicyEvaluationService, type PolicyEvaluationParams } from '@/utils/policyEvaluationService';
import { Device, PolicyEvaluationStep, type PolicyEvaluateResponse } from '@/models/flw-orchestrator.types';
import { ROUTES } from '@/constants/routes';
import { logScreenRender, logButtonClick, logBackNavigation, endPolicyEvaluationTiming } from '../../telemetry/telemetryEvents';
import { errorDev } from '@/logging/devConsole';
import authLogo from '@/assets/authenticator.png';

const IOS_URL = 'https://apps.apple.com/app/microsoft-authenticator/id983156458';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.azure.authenticator';

const AuthenticatorSetupScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const state = (location.state || {}) as {
    selectedDevice?: Device;
    policyParams?: PolicyEvaluationParams;
    mfaResult?: PolicyEvaluateResponse;
    isFirstScreenAfterAuth?: boolean;
  };

  const device = state.selectedDevice;
  const policyParams = state.policyParams;
  const mfaResult = state.mfaResult;
  const isFirstScreenAfterAuth = state.isFirstScreenAfterAuth ?? false;

  useEffect(() => {
    // Build UserBI data with MFA policy details
    const userbiData: Record<string, any> = {};
    
    if (mfaResult) {
      if (typeof mfaResult.isRequired === 'boolean') {
        userbiData.mfaPolicyIsRequired = mfaResult.isRequired;
      }
      if (mfaResult.policyType) {
        userbiData.mfaPolicyType = mfaResult.policyType;
      }
      if (mfaResult.reason) {
        userbiData.mfaPolicyReason = mfaResult.reason;
      }
      if (mfaResult.details) {
        userbiData.mfaPolicyDetails = mfaResult.details;
      }
    }
    
    // Log screen view (UserBI + marks 'auth-setup' step)
    logScreenRender('AuthenticatorSetupScreen', Object.keys(userbiData).length > 0 ? userbiData : undefined);
    // End MFA evaluation timing (screen rendered after MFA API call)
    endPolicyEvaluationTiming('mfa');
  }, [mfaResult]);
  const storeUrl = device === Device.ANDROID ? ANDROID_URL
  : device === Device.IOS ? IOS_URL
    : '';

  const handleNext = async () => {
    logButtonClick('AuthenticatorSetupScreen', 'next');

    if (device === Device.IOS) {
      navigate(ROUTES.APP_DOWNLOAD, { state: {
        selectedDevice: device,
        mfaResult,
        mamResult: { isRequired: false, reason: 'ios-not-applicable' },
      }});
      return;
    }

    if (!policyParams) {
      navigate(ROUTES.APP_DOWNLOAD, { state: {
        selectedDevice: device,
        mfaResult,
      }});
      return;
    }

    // Navigate to loading screen normally (without replace)
    // The loading screen will be removed when we navigate away from it using replace: true
    navigate(ROUTES.LOADING);

    const result = await PolicyEvaluationService.evaluateAndNavigate(dispatch, navigate, policyParams, {
      skipMfa: true,
      mfaResult,
    });

    if (!result.navigated || result.error) {
      errorDev('MAM evaluation error in AuthenticatorSetupScreen:', result.error);
      PolicyEvaluationService.navigateToRetry(navigate, PolicyEvaluationStep.MAM, policyParams, mfaResult, 0, true);
    }
  };

  const handleBack = () => {
    logBackNavigation('AuthenticatorSetupScreen');
    navigate(-1);
  };

  const instructions: InstructionStep[] = [
    { number: 1, text: t('authSetup.step1') },
    { number: 2, text: t('authSetup.step2') },
    { number: 3, text: t('authSetup.step3') },
    { number: 4, text: t('authSetup.step4') },
  ];

  return (
    <CardScreen
      title={t('authSetup.title')}
      qrCodeUrl={storeUrl}
      qrLogoUrl={authLogo}
      instructions={instructions}
      primaryButtonText={t('authSetup.next')}
      onPrimaryButtonClick={handleNext}
      secondaryLinkText={t('authSetup.cantScan')}
      secondaryLinkUrl={storeUrl}
      onBackClick={handleBack}
      logoAltText={t('footer.help')}
      showBackButton={!isFirstScreenAfterAuth}
    />
  );
};

export default AuthenticatorSetupScreen;
